import type { JSX } from 'react';
import {
  Controller,
  type Control,
  type FieldErrors,
  type FieldPath,
  type FieldValues,
  type UseFormSetValue,
} from 'react-hook-form';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { CustomField } from '@/types/machine-types';

/**
 * Plan 11-02 — CustomFieldValuesForm
 *
 * Renders one input per `CustomField` from a MachineType schema. The values
 * are written under the parent form's `customFieldValues` slice (RHF path
 * `customFieldValues.{key}` per the D-101 wire contract).
 *
 * Layout:
 *  - text       → shadcn `<Input type="text">`
 *  - number     → shadcn `<Input type="number">` (valueAsNumber: true)
 *  - date       → shadcn `<Input type="date">` (HTML5 date picker; ISO YYYY-MM-DD string)
 *  - dropdown   → shadcn `<Select>` populated from `field.dropdownOptions`
 *
 * Required-field markers (`*`) and inline `<FormMessage>`s drive the
 * `customFieldValues.{key}` Zod errors surfaced by the parent form.
 *
 * Empty schema (no rows): renders an informational alert (`cfv-empty`).
 */

// The parent form must declare a `customFieldValues: Record<string, unknown>` field.
// We type-narrow via a generic so callers can keep their richer FormValues shape.
export interface CustomFieldValuesFormProps<TFormValues extends FieldValues> {
  readonly schema: readonly CustomField[];
  readonly control: Control<TFormValues>;
  readonly setValue: UseFormSetValue<TFormValues>;
  readonly errors?: FieldErrors<TFormValues>;
  readonly disabled?: boolean;
  /** Override the RHF field-name prefix; defaults to `'customFieldValues'`. */
  readonly fieldsName?: string;
}

interface FieldErrorWithMessage {
  readonly message?: string;
}

function getFieldError(
  errors: FieldErrors | undefined,
  fieldsName: string,
  key: string,
): FieldErrorWithMessage | undefined {
  if (errors === undefined) return undefined;
  const slice = errors[fieldsName];
  if (slice === undefined || typeof slice !== 'object') return undefined;
  const candidate = (slice as Record<string, unknown>)[key];
  if (candidate === undefined || typeof candidate !== 'object') return undefined;
  const message = (candidate as { message?: unknown }).message;
  if (typeof message === 'string') {
    return { message };
  }
  return undefined;
}

/**
 * Coerce an arbitrary RHF value to a string for the controlled `<Input>`/`<Select>`
 * value prop. RHF stores `unknown` in this slice (per the wire shape) — `null`
 * and `undefined` collapse to `''` so React doesn't toggle controlled/uncontrolled.
 */
function toInputString(value: unknown): string {
  if (value === undefined || value === null) return '';
  if (typeof value === 'number') return Number.isFinite(value) ? String(value) : '';
  if (typeof value === 'string') return value;
  if (typeof value === 'boolean') return String(value);
  return '';
}

export function CustomFieldValuesForm<TFormValues extends FieldValues>({
  schema,
  control,
  setValue: _setValue,
  errors,
  disabled = false,
  fieldsName = 'customFieldValues',
}: CustomFieldValuesFormProps<TFormValues>): JSX.Element {
  if (schema.length === 0) {
    return (
      <Alert data-testid="cfv-empty">
        <AlertTitle>No custom fields</AlertTitle>
        <AlertDescription>This machine type has no custom fields defined.</AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-4" data-testid="cfv-root">
      {schema.map((field) => {
        // RHF FieldPath is a string-template type that doesn't accept dynamic
        // string concatenation under exactOptionalPropertyTypes. The runtime
        // is field-name based and tolerates the cast — same idiom Phase 10's
        // CustomFieldsBuilder uses for nested array indices.
        const path = `${fieldsName}.${field.key}` as FieldPath<TFormValues>;
        const fieldError = getFieldError(errors, fieldsName, field.key);
        const labelText = field.required ? `${field.label} *` : field.label;

        return (
          <div key={field.key} className="space-y-1" data-testid={`cfv-row-${field.key}`}>
            <Label htmlFor={`cfv-${field.key}`}>{labelText}</Label>

            {field.dataType === 'dropdown' && field.dropdownOptions !== null && (
              <Controller
                control={control}
                name={path}
                render={({ field: rhf }) => (
                  <Select
                    value={toInputString(rhf.value)}
                    onValueChange={(v) => {
                      rhf.onChange(v);
                    }}
                    disabled={disabled}
                  >
                    <SelectTrigger
                      id={`cfv-${field.key}`}
                      data-testid={`cfv-row-${field.key}-input`}
                      aria-invalid={fieldError !== undefined}
                    >
                      <SelectValue placeholder="Select an option…" />
                    </SelectTrigger>
                    <SelectContent>
                      {(field.dropdownOptions ?? []).map((opt, idx) => (
                        <SelectItem
                          key={opt}
                          value={opt}
                          data-testid={`cfv-row-${field.key}-option-${String(idx)}`}
                        >
                          {opt}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            )}

            {field.dataType === 'number' && (
              <Controller
                control={control}
                name={path}
                render={({ field: rhf }) => (
                  <Input
                    id={`cfv-${field.key}`}
                    type="number"
                    autoComplete="off"
                    disabled={disabled}
                    aria-invalid={fieldError !== undefined}
                    value={toInputString(rhf.value)}
                    onChange={(e) => {
                      const raw = e.target.value;
                      if (raw === '') {
                        rhf.onChange(null);
                        return;
                      }
                      const n = Number(raw);
                      rhf.onChange(Number.isFinite(n) ? n : raw);
                    }}
                    onBlur={rhf.onBlur}
                    ref={rhf.ref}
                    data-testid={`cfv-row-${field.key}-input`}
                  />
                )}
              />
            )}

            {field.dataType === 'date' && (
              <Controller
                control={control}
                name={path}
                render={({ field: rhf }) => (
                  <Input
                    id={`cfv-${field.key}`}
                    type="date"
                    autoComplete="off"
                    disabled={disabled}
                    aria-invalid={fieldError !== undefined}
                    value={toInputString(rhf.value)}
                    onChange={(e) => {
                      rhf.onChange(e.target.value === '' ? null : e.target.value);
                    }}
                    onBlur={rhf.onBlur}
                    ref={rhf.ref}
                    data-testid={`cfv-row-${field.key}-input`}
                  />
                )}
              />
            )}

            {field.dataType === 'text' && (
              <Controller
                control={control}
                name={path}
                render={({ field: rhf }) => (
                  <Input
                    id={`cfv-${field.key}`}
                    type="text"
                    autoComplete="off"
                    disabled={disabled}
                    aria-invalid={fieldError !== undefined}
                    value={toInputString(rhf.value)}
                    onChange={(e) => {
                      rhf.onChange(e.target.value);
                    }}
                    onBlur={rhf.onBlur}
                    ref={rhf.ref}
                    data-testid={`cfv-row-${field.key}-input`}
                  />
                )}
              />
            )}

            {fieldError?.message !== undefined && (
              <p className="text-xs text-destructive" data-testid={`cfv-row-${field.key}-error`}>
                {fieldError.message}
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
}
